import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { gmDisplayName } from '../../utils/displayName';
import api from '../../api/api';
import SeasonSelector from '../../components/analytics/SeasonSelector';

const GMRankings = () => {
  const { t } = useTranslation();
  const [seasonId, setSeasonId] = useState(null);
  const [rankings, setRankings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState('winPct');
  const [sortDir, setSortDir] = useState('desc');

  useEffect(() => {
    if (!seasonId) return;
    const fetch = async () => {
      setLoading(true);
      try {
        const res = await api.get(`/analytics/gm-rankings?seasonId=${seasonId}`);
        setRankings(res.data);
      } catch (err) { setRankings([]); }
      setLoading(false);
    };
    fetch();
  }, [seasonId]);

  const sortedRankings = [...rankings].sort((a, b) => {
    const valA = a[sortBy];
    const valB = b[sortBy];
    if (typeof valA === 'string') return sortDir === 'asc' ? valA.localeCompare(valB) : valB.localeCompare(valA);
    return sortDir === 'asc' ? valA - valB : valB - valA;
  });

  const handleSort = (field) => {
    if (sortBy === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortBy(field); setSortDir('desc'); }
  };

  const SortHeader = ({ field, children }) => (
    <th
      className="px-3 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase cursor-pointer hover:text-blue-600 dark:text-blue-400 select-none"
      onClick={() => handleSort(field)}
    >
      <span className="flex items-center gap-1">
        {children}
        {sortBy === field && <span>{sortDir === 'asc' ? '▲' : '▼'}</span>}
      </span>
    </th>
  );

  const FormBadges = ({ form }) => (
    <div className="flex gap-0.5">
      {form.map((r, i) => (
        <span key={i} className={`w-5 h-5 rounded-full text-xs font-bold flex items-center justify-center ${r === 'W' ? 'bg-green-500 text-white' : 'bg-red-500 text-white'}`}>
          {r}
        </span>
      ))}
    </div>
  );

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-800 dark:text-gray-200">👤 {t('analytics.gmRankings.title')}</h1>
          <p className="text-gray-500 dark:text-gray-400 text-sm mt-1">{t('analytics.gmRankings.subtitle')}</p>
        </div>
        <SeasonSelector selectedSeasonId={seasonId} onSeasonChange={setSeasonId} />
      </div>

      {loading ? (
        <div className="text-center py-12 text-gray-500 dark:text-gray-400">{t('common.loading')}</div>
      ) : rankings.length === 0 ? (
        <div className="text-center py-12 bg-white dark:bg-gray-800 rounded-xl shadow">
          <p className="text-gray-500 dark:text-gray-400 text-lg">{t('analytics.noData')}</p>
        </div>
      ) : (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow overflow-x-auto">
          <table className="w-full min-w-[950px]">
            <thead className="bg-gray-50 dark:bg-gray-800 border-b">
              <tr>
                <th className="px-3 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">#</th>
                <th className="px-3 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">{t('analytics.gm')}</th>
                <th className="px-3 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">{t('analytics.team')}</th>
                <SortHeader field="gamesPlayed">{t('analytics.cols.gp')}</SortHeader>
                <SortHeader field="wins">{t('analytics.cols.w')}</SortHeader>
                <SortHeader field="losses">{t('analytics.cols.l')}</SortHeader>
                <SortHeader field="winPct">{t('analytics.cols.pct')}</SortHeader>
                <SortHeader field="ppg">{t('analytics.cols.ppg')}</SortHeader>
                <SortHeader field="oppg">{t('analytics.cols.oppg')}</SortHeader>
                <SortHeader field="pointsDiff">{t('analytics.cols.diff')}</SortHeader>
                <th className="px-3 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">{t('analytics.cols.home')}</th>
                <th className="px-3 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">{t('analytics.cols.away')}</th>
                <th className="px-3 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">{t('analytics.cols.last5')}</th>
              </tr>
            </thead>
            <tbody>
              {sortedRankings.map((r, idx) => (
                <tr key={r.gm.id} className="border-b hover:bg-gray-50 dark:bg-gray-800 transition">
                  <td className="px-3 py-3 text-sm font-bold text-gray-400">{idx + 1}</td>
                  <td className="px-3 py-3">
                    <Link to={`/analytics/gm/${r.gm.id}`} className="font-semibold text-sm hover:text-blue-600 dark:text-blue-400 transition">
                      {gmDisplayName(r.gm)}
                    </Link>
                  </td>
                  <td className="px-3 py-3 text-sm text-gray-600 dark:text-gray-400">
                    {r.team?.abbreviation || '-'}
                  </td>
                  <td className="px-3 py-3 text-sm">{r.gamesPlayed}</td>
                  <td className="px-3 py-3 text-sm font-semibold text-green-600 dark:text-green-400">{r.wins}</td>
                  <td className="px-3 py-3 text-sm text-red-500">{r.losses}</td>
                  <td className="px-3 py-3 text-sm font-bold">{r.winPct.toFixed(3)}</td>
                  <td className="px-3 py-3 text-sm">{r.ppg}</td>
                  <td className="px-3 py-3 text-sm">{r.oppg}</td>
                  <td className={`px-3 py-3 text-sm font-semibold ${r.pointsDiff > 0 ? 'text-green-600 dark:text-green-400' : r.pointsDiff < 0 ? 'text-red-500' : ''}`}>
                    {r.pointsDiff > 0 ? '+' : ''}{r.pointsDiff}
                  </td>
                  <td className="px-3 py-3 text-sm">{r.homeRecord}</td>
                  <td className="px-3 py-3 text-sm">{r.awayRecord}</td>
                  <td className="px-3 py-3"><FormBadges form={r.last5 || []} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default GMRankings;
